/**
 * DispatcherCloseModal — force-close a stuck job's clock (DEC-315 option A).
 *
 * POST /v1/jobs/:id/dispatcher-close, the existing elevated-role route. It writes a STOPPED
 * timeclock event (triggered_by DISPATCHER_CLOSED) and does NOT change the job's status, so
 * the job stays open and stays on the stuck list, now marked "Clock stopped".
 *
 * The reason must be at least 8 characters AFTER trimming, matching the API's
 * `z.string().trim().min(8)` (DEC-277); it is sent trimmed. Mirrors CancelJobModal's
 * structure and styling. On success: invalidate ['jobs'] (which includes the stuck list),
 * toast, and call onClosed.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import type { StuckJobRow } from './use-stuck-jobs';

const REASON_MIN = 8;

interface CloseBody {
  reason: string;
}

export interface DispatcherCloseModalProps {
  readonly row: StuckJobRow | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called after a successful close so the parent can dismiss the modal. */
  readonly onClosed: () => void;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '80px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  resize: 'vertical',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--color-text-tertiary)',
  display: 'block',
  marginBottom: '4px',
};

export function DispatcherCloseModal({
  row,
  open,
  onClose,
  onClosed,
}: DispatcherCloseModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const mutation = useApiMutation<CloseBody, { ok: boolean }>(
    apiOpts,
    `/v1/jobs/${row?.jobId ?? ''}/dispatcher-close`,
    'POST',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        toast.success('Clock closed');
        onClosed();
      },
      onError: () => {
        setError('Could not close the clock. Try again.');
      },
    },
  );

  const reasonValid = reason.trim().length >= REASON_MIN;

  const handleConfirm = React.useCallback((): void => {
    if (!reasonValid || row === null) return;
    setError(null);
    mutation.mutate({ reason: reason.trim() });
  }, [reasonValid, row, reason, mutation]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{`Close clock — ${row?.haulId ?? 'job'}`}</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
            This stops the driver&apos;s clock (recorded as dispatcher-closed). The job stays open
            and stays on this list.
          </p>
          <label htmlFor="dispatcher-close-reason" style={labelStyle}>
            Reason
          </label>
          <textarea
            id="dispatcher-close-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="Why is this clock being closed? (min 8 characters)"
            style={textareaStyle}
            aria-label="Close reason"
          />
          {reason.trim().length > 0 && !reasonValid && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Reason must be at least {REASON_MIN} characters.
            </span>
          )}
        </div>

        {error !== null && (
          <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Keep running
          </Button>
          <Button
            type="button"
            disabled={!reasonValid || mutation.isPending}
            onClick={handleConfirm}
            style={{
              backgroundColor: 'var(--color-danger)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-danger)',
              opacity: !reasonValid || mutation.isPending ? 0.6 : 1,
            }}
          >
            {mutation.isPending ? 'Closing…' : 'Close clock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
